import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Lock, AlertTriangle, Mail, Phone, Eye, EyeOff, Clock, Loader2, UserPlus } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvitePage() {
  const { token: pathToken, username: pathUsername, code: pathCode } = useParams();
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get('ref');
  
  // Support both long token and short code URLs
  // Long: /invite/:token
  // Short: /j/:username/:code
  const isShortUrl = pathUsername && pathCode;
  const token = pathToken || queryToken;
  
  const navigate = useNavigate();
  
  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null);
  
  // Registration form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registering, setRegistering] = useState(false);
  
  // For link invites - OTP verification
  const [contactType, setContactType] = useState('email');
  const [contactValue, setContactValue] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verified, setVerified] = useState(false);
  
  // Session tracking
  const sessionId = useRef(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Password validation
  const passwordErrors = [];
  if (password.length > 0) {
    if (password.length < 8) passwordErrors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) passwordErrors.push('Uppercase letter');
    if (!/[a-z]/.test(password)) passwordErrors.push('Lowercase letter');
    if (!/[0-9]/.test(password)) passwordErrors.push('Number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) passwordErrors.push('Special character');
  }
  const passwordValid = password.length >= 8 && passwordErrors.length === 0;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  
  // Load invite info
  useEffect(() => {
    // For short URLs, we need both username and code
    if (isShortUrl) {
      if (!pathUsername || !pathCode) {
        setError('Invalid invite link');
        setLoading(false);
        return;
      }
      
      // Fetch using short code endpoint
      axios.get(`${API}/api/invite/short/${pathUsername}/${pathCode}`)
        .then(response => {
          setInviteInfo(response.data);
          setLoading(false);
          
          if (response.data.is_existing_user) {
            return;
          }
          
          if (response.data.invite_method === 'email' || response.data.invite_method === 'sms') {
            setVerified(true);
          }
        })
        .catch(err => {
          console.error('Invite load error:', err);
          setError('This invite is no longer valid');
          setLoading(false);
        });
      return;
    }
    
    // For long token URLs
    if (!token) {
      setError('Invalid invite link');
      setLoading(false);
      return;
    }
    
    axios.get(`${API}/api/invite/${token}`)
      .then(response => {
        setInviteInfo(response.data);
        setLoading(false);
        
        // For existing users, they should login instead of signup
        if (response.data.is_existing_user) {
          // Don't set verified - they need to login
          return;
        }
        
        // For email/sms invites to NEW users, pre-set verification as done
        if (response.data.invite_method === 'email' || response.data.invite_method === 'sms') {
          setVerified(true);
        }
      })
      .catch(err => {
        console.error('Invite load error:', err);
        setError('This invite is no longer valid');
        setLoading(false);
      });
  }, [token, isShortUrl, pathUsername, pathCode]);
  
  // Send OTP for link invites
  const sendOtp = async () => {
    if (!contactValue) {
      toast.error(`Please enter your ${contactType}`);
      return;
    }
    
    setSendingOtp(true);
    try {
      await axios.post(`${API}/api/invite/${token}/send-otp`, {
        contact_type: contactType,
        contact_value: contactValue
      });
      setOtpSent(true);
      toast.success(`Verification code sent to your ${contactType}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingOtp(false);
    }
  };
  
  // Verify OTP with backend
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  
  const verifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter 6-digit code');
      return;
    }
    
    setVerifyingOtp(true);
    try {
      await axios.post(`${API}/api/invite/${token}/verify-otp`, {
        contact_type: contactType,
        contact_value: contactValue,
        otp: otp
      });
      setVerified(true);
      toast.success('Verified successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifyingOtp(false);
    }
  };
  
  // Register from invite
  const handleRegister = async () => {
    // Validation
    if (!username || username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      toast.error('Username can only contain letters, numbers, and underscores');
      return;
    }
    if (!passwordValid) {
      toast.error('Please fix password requirements');
      return;
    }
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }
    
    // For link invites, email is required
    if (inviteInfo?.invite_method === 'copy' && !verified && !contactValue) {
      toast.error('Please enter your email to continue');
      return;
    }
    
    setRegistering(true);
    try {
      const payload = {
        username: username.toLowerCase(),
        password,
        session_id: sessionId.current
      };
      
      // For link invites, include email directly (no OTP required)
      if (inviteInfo?.invite_method === 'copy') {
        if (verified) {
          // If user went through OTP verification
          payload.contact_type = contactType;
          payload.contact_value = contactValue;
          payload.otp = otp;
        } else if (contactValue) {
          // Direct email registration (simpler UX)
          payload.email = contactValue;
        }
      }
      
      // Use the full token from inviteInfo (important for short URLs)
      const registrationToken = inviteInfo?.token || token;
      const response = await axios.post(`${API}/api/invite/${registrationToken}/register`, payload);
      
      // Store auth
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify({
        id: response.data.user_id,
        username: response.data.username
      }));
      
      toast.success('Welcome to Privluxe!');
      
      // Navigate to the conversation
      navigate(`/chat/${response.data.conversation_id}`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Registration failed';
      toast.error(errorMsg);
    } finally {
      setRegistering(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #020617 100%)' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#7C3AED' }} />
          <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Loading invite...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #020617 100%)' }}>
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
            <AlertTriangle className="h-8 w-8" style={{ color: '#EF4444' }} />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#E5E7EB' }}>Invite Not Found</h1>
          <p className="mb-6" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>{error}</p>
          <Button onClick={() => navigate('/')} style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}>
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }
  
  // EXISTING USER - Show friend request UI with login redirect
  if (inviteInfo?.is_existing_user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #020617 100%)' }}>
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold tracking-widest mb-2" style={{ color: '#E5E7EB' }}>PRIVLUXE</h1>
            <Shield className="h-5 w-5 mx-auto" style={{ color: '#7C3AED' }} />
          </div>
          
          {/* Main Card */}
          <div className="rounded-2xl p-6" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            {/* Sender Info */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}>
                {inviteInfo?.sender_display_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <h2 className="text-xl font-semibold" style={{ color: '#E5E7EB' }}>
                {inviteInfo?.sender_display_name} sent you a friend request
              </h2>
              <p className="text-sm mt-1" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                on Privluxe - Secure Private Messaging
              </p>
            </div>
            
            {/* Message Preview */}
            {inviteInfo?.message_preview && (
              <div className="mb-6 p-4 rounded-xl text-center" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
                <p className="text-lg" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                  "{inviteInfo.message_preview}"
                </p>
              </div>
            )}
            
            {/* Friend Request Info */}
            <div className="mb-6 p-4 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <div className="flex items-start gap-3">
                <UserPlus className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#22C55E' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#22C55E' }}>Friend Request</p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                    Login to your account to accept or decline this friend request
                  </p>
                </div>
              </div>
            </div>
            
            {/* Expiry Warning */}
            <div className="mb-6 p-3 rounded-lg flex items-center gap-2" style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
              <Clock className="h-4 w-4 shrink-0" style={{ color: '#F59E0B' }} />
              <p className="text-xs" style={{ color: '#F59E0B' }}>
                This friend request expires in {inviteInfo?.expires_in_hours || 48} hours
              </p>
            </div>
            
            {/* Login Button */}
            <Button
              type="button"
              onClick={() => navigate('/login', { state: { returnTo: `/invite/${token}`, friendRequest: true } })}
              className="w-full h-12"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              data-testid="invite-login-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              Login Securely
            </Button>
            
            {/* Privacy Notice */}
            <p className="text-center text-xs mt-4" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
              <Lock className="h-3 w-3 inline mr-1" />
              End-to-end encrypted connection
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #020617 100%)' }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-widest mb-2" style={{ color: '#E5E7EB' }}>PRIVLUXE</h1>
          <Shield className="h-5 w-5 mx-auto" style={{ color: '#7C3AED' }} />
        </div>
        
        {/* Main Card */}
        <div className="rounded-2xl p-6" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          {/* Sender Info */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}>
              {inviteInfo?.sender_display_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <h2 className="text-xl font-semibold" style={{ color: '#E5E7EB' }}>
              {inviteInfo?.sender_display_name} wants to send you a message
            </h2>
            <p className="text-sm mt-1" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
              on Privluxe - Secure Private Messaging
            </p>
          </div>
          
          {/* Message Preview */}
          {inviteInfo?.message_preview && (
            <div className="mb-6 p-4 rounded-xl text-center" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
              <p className="text-lg" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                "{inviteInfo.message_preview}"
              </p>
            </div>
          )}
          
          {/* Urgency Message */}
          <div className="mb-6 p-3 rounded-lg flex items-center gap-2" style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
            <Clock className="h-4 w-4 shrink-0" style={{ color: '#F59E0B' }} />
            <p className="text-xs" style={{ color: '#F59E0B' }}>
              This invite expires in {inviteInfo?.expires_in_hours || 48} hours.
            </p>
          </div>
          
          {/* SIGN UP FORM - All fields in one form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label style={{ color: '#E5E7EB' }}>Email or Phone</Label>
              <Input
                type="text"
                value={contactValue}
                onChange={(e) => { 
                  setContactValue(e.target.value);
                  setContactType(e.target.value.includes('@') ? 'email' : 'phone');
                }}
                placeholder="Enter your email or phone"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                data-testid="invite-contact-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label style={{ color: '#E5E7EB' }}>Choose a Username</Label>
              <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="Choose a username"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  data-testid="invite-username-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label style={{ color: '#E5E7EB' }}>Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 pr-10"
                    data-testid="invite-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(229, 231, 235, 0.5)' }}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {password.length > 0 && passwordErrors.length > 0 && (
                  <p className="text-xs" style={{ color: '#EF4444' }}>
                    Missing: {passwordErrors.join(', ')}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label style={{ color: '#E5E7EB' }}>Confirm Password</Label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  data-testid="invite-confirm-password-input"
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs" style={{ color: '#EF4444' }}>Passwords do not match</p>
                )}
              </div>
              
              <Button
                onClick={handleRegister}
                disabled={registering || !username || !passwordValid || !passwordsMatch}
                className="w-full h-12 text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                data-testid="invite-register-btn"
              >
                {registering ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating Account...</>
                ) : (
                  <>Join Conversation</>
                )}
              </Button>
              
              {/* Privacy Notice */}
              <div className="mt-4 flex items-start gap-2">
                <Lock className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#22C55E' }} />
                <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                  Your messages are end-to-end encrypted. We never share your contact information.
                </p>
              </div>
            </div>
        </div>
        
        {/* Footer - Login option */}
        <p className="text-center mt-6 text-sm" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
          Already have an account?{' '}
          <button onClick={() => navigate('/login')} className="underline font-medium" style={{ color: '#7C3AED' }}>
            Sign in
          </button>
        </p>
      </div>
      
      {/* Phone input styles */}
      <style>{`
        .phone-input-dark .PhoneInputInput {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.5rem;
          padding: 0.75rem 1rem;
          color: white;
          font-size: 16px;
          height: 48px;
        }
        .phone-input-dark .PhoneInputInput:focus {
          border-color: #7C3AED;
          outline: none;
        }
        .phone-input-dark .PhoneInputCountrySelect {
          background: rgba(255, 255, 255, 0.05);
          color: white;
        }
      `}</style>
    </div>
  );
}
